import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
import { DayOfWeek, HOURS_IN_DAY, IconNames, SchedulingType } from '../../../../common/constants';
import { ValidationService } from '../../../../common/services';
import { AutoUnsubscribe } from '../../../../common/decorators';
import { debounceTime, distinctUntilChanged, filter, map, skip, switchMap } from 'rxjs/operators';
import { isNil } from 'lodash';
import { SvgIconComponent } from '../../../svg-icon/svg-icon.component';
import { ChipFieldComponent } from '../../../chip-field';
import { FieldErrorMessages } from '../../../field-error-messages/field-error-messages.component';

@Component({
  selector: 'ui-monitoring-profile-subscription',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatSelectModule,
    SvgIconComponent,
    ChipFieldComponent,
    FieldErrorMessages,
  ],
  templateUrl: './monitoring-profile-subscription.component.html',
  styleUrls: ['./monitoring-profile-subscription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class MonitoringProfileSubscriptionComponent {
  @Input() set subscription(form: UntypedFormGroup) {
    if (form) {
      this.subscriptions = [];
      this.initSubscriptionForm(form);
    }
  }
  @Input() isSubscriptionCloned = false;
  @Input() set isMultiHoursSubscription(value: boolean) {
    this.isMultiHours$.next(value);
  }
  @Input() subscriptionTitleTranslationKey = 'monitoring.profile.item.subscription';
  @Output()
  isSubscriptionEnabled: EventEmitter<boolean> = new EventEmitter<boolean>();
  subscriptionForm: UntypedFormGroup;
  subscriptionStateControl = new UntypedFormControl(true);
  schedulingTypeControl: UntypedFormControl;
  schedulingType = SchedulingType;
  dayOfWeekOptions: string[] = Object.values(DayOfWeek);
  hourOptions: number[] = [...Array(HOURS_IN_DAY).keys()];
  subscriptions: Subscription[] = [];
  weeklyHoursControl = new UntypedFormControl(null, [Validators.required]);
  dailyHoursControl = new UntypedFormControl(null, [Validators.required]);
  isMultiHours$ = new BehaviorSubject<boolean>(false);
  iconNameList = IconNames;

  constructor(public validationService: ValidationService) {}

  private initSubscriptionForm(form: UntypedFormGroup): void {
    this.subscriptionForm = form;
    this.schedulingTypeControl = form.get('schedulingType') as UntypedFormControl;
    const dayControl = form.get('day') as UntypedFormControl;
    const hoursControl = form.get('hours') as UntypedFormControl;
    this.schedulingTypeControl.value === SchedulingType.Weekly
      ? this.weeklyHoursControl.setValue(hoursControl.value && hoursControl.value[0])
      : this.dailyHoursControl.setValue(hoursControl.value);

    if (!this.subscriptionStateControl.value) {
      this.subscriptionForm.disable();
    }

    this.subscriptions.push(
      this.schedulingTypeControl.valueChanges.subscribe((value: SchedulingType) => {
        value === SchedulingType.Weekly
          ? dayControl.setValidators([Validators.required])
          : dayControl.clearValidators();
        dayControl.updateValueAndValidity({ emitEvent: false });
      }),
      this.subscriptionStateControl.valueChanges.subscribe((value: boolean) => {
        this.isSubscriptionEnabled.emit(value);
        if (value) {
          this.subscriptionForm.enable();
          this.weeklyHoursControl.enable();
          this.dailyHoursControl.enable();
        } else {
          this.subscriptionForm.disable();
          this.subscriptionForm.clearValidators();
          this.subscriptionForm.updateValueAndValidity({ emitEvent: false });
          this.weeklyHoursControl.disable();
          this.dailyHoursControl.disable();
        }
        if (this.subscriptionForm.pristine) {
          this.subscriptionForm.markAsDirty();
        }
      }),
      this.isMultiHours$
        .pipe(
          filter(Boolean),
          switchMap(() =>
            merge(this.weeklyHoursControl.valueChanges, this.dailyHoursControl.valueChanges).pipe(
              debounceTime(0),
              filter((value: number[] | number | null) => !isNil(value)),
              map((value: number[] | number) => (typeof value === 'number' ? [value] : value)),
            ),
          ),
        )
        .subscribe((value: number[]) => {
          hoursControl.setValue(value);
          this.subscriptionForm.markAsDirty();
        }),
      this.isMultiHours$
        .pipe(
          filter(Boolean),
          switchMap(() =>
            this.schedulingTypeControl.valueChanges.pipe(filter(Boolean), distinctUntilChanged(), skip(1)),
          ),
        )
        .subscribe(() => {
          this.weeklyHoursControl.reset();
          this.dailyHoursControl.reset();
          hoursControl.reset();
        }),
    );
    if (!this.isSubscriptionCloned) {
      this.subscriptionStateControl.setValue(!!form.get('id').value);
    }
    this.subscriptionForm.markAsPristine();
  }
}
